import React, { useState, useEffect, useRef } from 'react';
import { FiType, FiX, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import './ArabicKeyboard.css';

const ArabicKeyboard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeInput, setActiveInput] = useState(null);
  const keyboardRef = useRef(null);

  // Arabic keyboard layout
  const arabicKeys = [
    ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج', 'د'],
    ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط', 'ئ'],
    ['ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ', 'د', 'ذ', 'إ'],
    ['أ', 'آ', 'إ', 'أ', 'ؤ', 'ئ', 'ا', 'ب', 'ت', 'ث', 'ج', 'ح'],
    ['خ', 'د', 'ذ', 'ر', 'ز', 'س', 'ش', 'ص', 'ض', 'ط', 'ظ', 'ع'],
    ['غ', 'ف', 'ق', 'ك', 'ل', 'م', 'ن', 'ه', 'و', 'ي', 'ى', 'ة']
  ];

  // Numbers and symbols
  const numberKeys = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['-', '/', ':', ';', '(', ')', '$', '&', '@', '"'],
    ['.', ',', '?', '!', '\'', '`', '~', '^', '*', '+'],
    ['=', '[', ']', '{', '}', '\\', '|', '<', '>', '%']
  ];

  const [isShift, setIsShift] = useState(false);
  const [isNumbers, setIsNumbers] = useState(false);

  useEffect(() => {
    // Add event listeners for input focus
    const handleInputFocus = (e) => {
      if (isOpen) {
        setActiveInput(e.target);
      }
    };

    const handleInputBlur = () => {
      // Don't clear activeInput immediately to allow keyboard interaction
      setTimeout(() => {
        if (!keyboardRef.current?.contains(document.activeElement)) {
          setActiveInput(null);
        }
      }, 100);
    };

    // Add listeners to all input and textarea elements
    const inputs = document.querySelectorAll('input[type="text"], input[type="search"], textarea');
    inputs.forEach(input => {
      input.addEventListener('focus', handleInputFocus);
      input.addEventListener('blur', handleInputBlur);
    });

    return () => {
      inputs.forEach(input => {
        input.removeEventListener('focus', handleInputFocus);
        input.removeEventListener('blur', handleInputBlur);
      });
    };
  }, [isOpen]);

  const insertText = (text) => {
    if (!activeInput) return;

    const start = activeInput.selectionStart;
    const end = activeInput.selectionEnd;
    const value = activeInput.value;

    // Insert the text at cursor position
    const newValue = value.substring(0, start) + text + value.substring(end);
    activeInput.value = newValue;

    // Set cursor position after inserted text
    const newCursorPos = start + text.length;
    activeInput.setSelectionRange(newCursorPos, newCursorPos);

    // Trigger input event to update React state
    const event = new Event('input', { bubbles: true });
    activeInput.dispatchEvent(event);

    // Focus back to input
    activeInput.focus();
  };

  const handleKeyClick = (key, e) => {
    // Prevent the button from stealing focus
    e.preventDefault();
    e.stopPropagation();
    
    // Ensure we have an active input
    if (!activeInput) {
      // Try to find the currently focused input
      const focusedElement = document.activeElement;
      if (focusedElement && (focusedElement.tagName === 'INPUT' || focusedElement.tagName === 'TEXTAREA')) {
        setActiveInput(focusedElement);
      } else {
        return; // No active input found
      }
    }

    switch (key) {
      case 'space':
        insertText(' ');
        break;
      case 'backspace':
        if (activeInput) {
          const start = activeInput.selectionStart;
          const end = activeInput.selectionEnd;
          const value = activeInput.value;

          if (start === end) {
            // Delete one character before cursor
            const newValue = value.substring(0, start - 1) + value.substring(end);
            activeInput.value = newValue;
            activeInput.setSelectionRange(start - 1, start - 1);
          } else {
            // Delete selected text
            const newValue = value.substring(0, start) + value.substring(end);
            activeInput.value = newValue;
            activeInput.setSelectionRange(start, start);
          }

          const event = new Event('input', { bubbles: true });
          activeInput.dispatchEvent(event);
          activeInput.focus();
        }
        break;
      case 'enter':
        insertText('\n');
        break;
      case 'shift':
        setIsShift(!isShift);
        break;
      case 'numbers':
        setIsNumbers(!isNumbers);
        break;
      default:
        insertText(key);
    }
  };

  const toggleKeyboard = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Find the currently focused input when opening
      const focusedElement = document.activeElement;
      if (focusedElement && (focusedElement.tagName === 'INPUT' || focusedElement.tagName === 'TEXTAREA')) {
        setActiveInput(focusedElement);
      }
    } else {
      setActiveInput(null);
    }
  };

  const renderKeys = () => {
    if (isNumbers) {
      return numberKeys.map((row, rowIndex) => (
        <div key={rowIndex} className="keyboard-row">
          {row.map((key) => (
            <button
              key={key}
              className="keyboard-key"
              onClick={(e) => handleKeyClick(key, e)}
              onMouseDown={(e) => e.preventDefault()}
            >
              {key}
            </button>
          ))}
        </div>
      ));
    }

    return arabicKeys.map((row, rowIndex) => (
      <div key={rowIndex} className="keyboard-row">
        {row.map((key) => (
                      <button
              key={key}
              className="keyboard-key"
              onClick={(e) => handleKeyClick(key, e)}
              onMouseDown={(e) => e.preventDefault()}
            >
              {key}
            </button>
        ))}
      </div>
    ));
  };

  if (!isOpen) {
    return (
      <div className="arabic-keyboard-toggle">
        <button
          className="keyboard-toggle-btn"
          onClick={toggleKeyboard}
          title="Toggle Arabic Keyboard"
        >
          <FiType size={20} />
          <span>Arabic</span>
        </button>
      </div>
    );
  }

  return (
    <div className="arabic-keyboard-container">
      {/* Toggle button when keyboard is open */}
      <div className={`keyboard-header ${activeInput ? 'has-active-input' : ''}`}>
        <button
          className="keyboard-toggle-btn active"
          onClick={toggleKeyboard}
          title="Close Arabic Keyboard"
        >
          <FiType size={20} />
          <span>Arabic</span>
        </button>
        
        <div className="keyboard-controls">
          <button
            className="keyboard-control-btn"
            onClick={toggleKeyboard}
            title="Close"
          >
            <FiX size={16} />
          </button>
        </div>
      </div>

      {/* Keyboard content */}
        <div className="keyboard-content" ref={keyboardRef}>
          <div className="keyboard-layout">
            {renderKeys()}
            

          </div>
        </div>
    </div>
  );
};

export default ArabicKeyboard;
